import React, { useEffect, useState } from 'react';
import { Button, Col, Form, Input, Row, Table, Tag, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useLazyQuery } from '@apollo/client';
import TablePaginationBar from 'components/TablePaginationBar';
import {
    TMS_TASK_RELATED_LOADINGS,
    TmsTaskRelatedFilter,
    TmsTaskRelatedLoadingItem,
    TmsTaskRelatedLoadingsResponse,
} from '../graphql/queries/taskRelatedLookupQueries';
import {
    BUSINESS_TYPE_MAP,
    DEFAULT_PAGINATION,
    PAGE_SIZE_OPTIONS,
    TASK_TYPE_MAP,
    parseIdInput,
    renderTagged,
    renderTaskStatus,
    renderText,
    renderTime,
} from './taskManagementShared';

type FilterValues = {
    trackingNumber?: string;
    orderId?: string;
    bagNumber?: string;
    bagId?: string;
};

type Props = {
    /** 「查看任务详情」→ 切到任务管理 Tab 按主键ID查 */
    onViewTask: (taskId: number) => void;
};

/**
 * 运单/包牌关联任务：按 运单号 / 运单ID / 包牌号 / 包牌ID 反查装载明细及所属任务基础信息。
 * 首次打开不自动查询（无条件反查无意义），至少填一项。详情统一到任务管理 Tab 查看。
 */
const TaskRelatedLookupTab: React.FC<Props> = ({ onViewTask }) => {
    const [form] = Form.useForm<FilterValues>();
    const [activeFilter, setActiveFilter] = useState<TmsTaskRelatedFilter | null>(null);
    const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
    const [runQuery, { data, loading }] = useLazyQuery<TmsTaskRelatedLoadingsResponse>(TMS_TASK_RELATED_LOADINGS, {
        fetchPolicy: 'network-only',
        onError: (e) => message.error(e.message),
    });

    useEffect(() => {
        if (activeFilter === null) return;
        runQuery({
            variables: {
                filter: activeFilter,
                pageRequest: { page: pagination.current, size: pagination.pageSize },
            },
        });
    }, [activeFilter, pagination, runQuery]);

    const handleSearch = () => {
        const values = form.getFieldsValue();
        const orderId = parseIdInput(values.orderId);
        const bagId = parseIdInput(values.bagId);
        if (orderId === null || bagId === null) {
            message.warning('ID 必须是纯数字');
            return;
        }
        const trackingNumber = values.trackingNumber?.trim() || undefined;
        const bagNumber = values.bagNumber?.trim() || undefined;
        if (orderId === undefined && trackingNumber === undefined && bagId === undefined && bagNumber === undefined) {
            message.warning('请至少填写一个条件：运单号 / 运单ID / 包牌号 / 包牌ID');
            return;
        }
        setPagination((prev) => ({ ...prev, current: 1 }));
        setActiveFilter({ orderId, trackingNumber, bagId, bagNumber });
    };

    const columns: ColumnsType<TmsTaskRelatedLoadingItem> = [
        { title: '任务ID', dataIndex: 'taskId', width: 90, fixed: 'left', render: renderText },
        {
            title: '任务号',
            dataIndex: 'taskNumber',
            width: 200,
            fixed: 'left',
            // 已删任务仍带基础信息，任务号划线置灰、悬浮提示「已删除」（用户 2026-10-21：不要多占一行的 Tag）；任务行不存在才显示「任务不存在」
            render: (v: string | null, record) => (record.taskMissing ? <Tag color="red">任务不存在</Tag> : record.taskDeleted ? (
                <span className="line-through text-gray-400" title="任务已逻辑删除">{v ?? '-'}</span>
            ) : renderText(v)),
        },
        { title: '状态', dataIndex: 'taskStatus', width: 100, render: renderTaskStatus },
        { title: '任务类型', dataIndex: 'taskType', width: 100, render: renderTagged(TASK_TYPE_MAP) },
        { title: '业务类型', dataIndex: 'businessType', width: 110, render: renderTagged(BUSINESS_TYPE_MAP) },
        { title: '任务日期', dataIndex: 'taskDate', width: 110, render: renderText },
        { title: '路线ID', dataIndex: 'routeId', width: 90, render: renderText },
        { title: '路线名称', dataIndex: 'routeName', width: 180, ellipsis: true, render: renderText },
        { title: '装车途经点ID', dataIndex: 'waypointId', width: 120, render: renderText },
        { title: '装车时间(UTC)', dataIndex: 'createdAt', width: 170, render: renderTime },
        { title: '包牌ID', dataIndex: 'bagId', width: 100, render: renderText },
        { title: '包牌号', dataIndex: 'bagNumber', width: 160, render: renderText },
        { title: '装载明细ID', dataIndex: 'loadingDetailId', width: 110 },
        {
            title: '操作',
            key: 'action',
            width: 120,
            fixed: 'right',
            render: (_, record) => (
                <Button
                    type="link"
                    size="small"
                    disabled={record.taskMissing || record.taskId === null}
                    onClick={() => record.taskId !== null && onViewTask(record.taskId)}
                >
                    查看任务详情
                </Button>
            ),
        },
    ];

    const rows = data?.opsToolTmsTaskRelatedLoadings.content ?? [];
    const totalElements = data?.opsToolTmsTaskRelatedLoadings.totalElements ?? 0;

    return (
        <div className="space-y-4">
            <Form form={form}>
                <Row gutter={16}>
                    <Col span={6}>
                        <Form.Item name="trackingNumber" label="运单号">
                            <Input allowClear placeholder="精确匹配" />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="orderId" label="运单ID">
                            <Input allowClear placeholder="精确匹配" />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="bagNumber" label="包牌号">
                            <Input allowClear placeholder="精确匹配" />
                        </Form.Item>
                    </Col>
                    <Col span={6}>
                        <Form.Item name="bagId" label="包牌ID">
                            <Input allowClear placeholder="精确匹配" />
                        </Form.Item>
                    </Col>
                    <Col span={24} className="text-right">
                        <Form.Item>
                            <Button className="mr-2" onClick={() => form.resetFields()}>
                                重置
                            </Button>
                            <Button type="primary" loading={loading} onClick={handleSearch}>
                                查询
                            </Button>
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
            <Table<TmsTaskRelatedLoadingItem>
                rowKey="loadingDetailId"
                size="small"
                loading={loading}
                columns={columns}
                dataSource={rows}
                pagination={false}
                scroll={{ x: 1660 }}
                className="mb-4"
                locale={{ emptyText: activeFilter === null ? '请输入运单号 / 运单ID / 包牌号 / 包牌ID 后查询' : undefined }}
            />
            <TablePaginationBar
                total={totalElements}
                current={pagination.current}
                pageSize={pagination.pageSize}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                onChange={(page, size) => setPagination({ current: page, pageSize: size })}
            />
        </div>
    );
};

export default TaskRelatedLookupTab;
